// `extension-alert-candidates-message/email-sign-in` — "Please connect your work email", the
// panel that stands in for the message composer until the user's mailbox is linked.
//
// It renders in FOUR places: beside the candidate profile and beside the review screen, in both
// products. Only two things vary between them — whether the ✕ is there, and where it closes to.
import { createElement, useState, type ReactElement } from 'react'
import { asset } from '../assetBase.ts'
import { ExchangeLoginModal } from './ExchangeLoginModal.tsx'

/**
 * `EmailServerType` (extension-alert-candidates-message.api.ng1.ts:45).
 *
 * ⚠️ IT IS A NUMERIC ENUM AND ITS FIRST MEMBER IS ZERO, which is the whole reason the states
 * read `$stateParams.emailServerType >= 0 ? … : null` rather than testing truthiness: Gmail is
 * `0`, and `!!0` would turn "the user has Gmail" into "the user has nothing configured".
 */
export const EmailServerType = { Gmail: 0, Outlook: 1, Exchange: 2 } as const
export type EmailServerType = (typeof EmailServerType)[keyof typeof EmailServerType]

/** `$stateParams.emailServerType >= 0 ? it : null` — the parse both call sites share. */
export function parseEmailServerType(raw: string | null | undefined): EmailServerType | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined
  const parsed = Number.parseInt(raw, 10)
  if (Number.isNaN(parsed) || parsed < 0) return undefined
  return parsed as EmailServerType
}

/**
 * Which connect buttons the panel offers.
 *
 * ⚠️ EXCHANGE IS NOT OFFERED BY DEFAULT AND THE OTHER TWO ARE. Read the three `ng-if`s together
 * and the asymmetry is the point: with nothing configured, Google and Outlook both show, while
 * Exchange appears ONLY when the tenant has explicitly forced it (`forceEmailServerType ===
 * Exchange`) or the user is already on it. Writing the three conditions as one symmetrical rule
 * puts a Microsoft Exchange button in front of every tenant.
 */
export function emailSignInProviders(
  emailServerType: EmailServerType | undefined,
  forceEmailServerType: EmailServerType | undefined,
): { gmail: boolean; outlook: boolean; exchange: boolean } {
  const unset = emailServerType === undefined
  return {
    gmail:
      (unset && (forceEmailServerType === undefined || forceEmailServerType === EmailServerType.Gmail)) ||
      emailServerType === EmailServerType.Gmail,
    outlook:
      (unset && (forceEmailServerType === undefined || forceEmailServerType === EmailServerType.Outlook)) ||
      emailServerType === EmailServerType.Outlook,
    exchange:
      (unset && forceEmailServerType === EmailServerType.Exchange) || emailServerType === EmailServerType.Exchange,
  }
}

export interface EmailSignInProps {
  /**
   * ⚠️ THE ✕ IS PRESENT ONLY WITH AN ALERT ID, and that is how the two call sites differ. The
   * review screen passes one and gets a close button; the candidate profile does not pass it at
   * all and gets none, because there the panel IS the screen's right half rather than an
   * overlay on a list. `ng-if="!!ctrl.alertId"` — absent, not merely disabled.
   */
  alertId?: number
  emailServerType?: EmailServerType
  /** `window.careero.forceEmailServerType`; see `emailSignInProviders`. */
  forceEmailServerType?: EmailServerType
  /** `flex-basis` on the component element — Extended at all four call sites. */
  flexBasis?: number
  /**
   * The component ELEMENT to render as.
   *
   * ⚠️ THE STANDALONE SCREEN IS A DIFFERENT ELEMENT WITH THE SAME TEMPLATE.
   * `/chrome-extension/email-signin` renders `<extension-user-external-email-signin>` and the
   * side-view panel renders `<extension-alert-candidates-email-signin>`; the markup inside is
   * the same template minus the ✕, and the two stylesheets are written against the two element
   * names. Rendering the panel's element on the standalone route would pull the panel's
   * flex rules onto a screen that is the whole page.
   */
  hostElement?: string
  onClose?: () => void
  onConnect?: (provider: 'gmail' | 'outlook' | 'exchange') => void
  /**
   * `extensionAlertExchangeLoginModalService.show()` → `validateAndSave`. Exchange is not an OAuth
   * round-trip: the button raises "Login to your exchange" here, and this resolves `true` when the
   * tenant accepted the credentials. Absent, the Exchange button raises nothing.
   */
  onExchangeSignIn?: (username: string, password: string) => Promise<boolean>
  /** After a successful Exchange sign-in, once the dialog has closed. */
  onExchangeConnected?: () => void
}

export function EmailSignIn({
  alertId,
  emailServerType,
  forceEmailServerType,
  flexBasis,
  hostElement = 'extension-alert-candidates-email-signin',
  onClose,
  onConnect,
  onExchangeSignIn,
  onExchangeConnected,
}: EmailSignInProps): ReactElement {
  const providers = emailSignInProviders(emailServerType, forceEmailServerType)
  const [exchangeOpen, setExchangeOpen] = useState(false)

  const content = (
    <div className="extension-email-signin-container">
      {alertId === undefined ? null : (
        <div className="extension-email-signin-x-icon">
          <i
            className="far fa-times"
            role="button"
            aria-label="Close"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') onClose?.()
            }}
          />
        </div>
      )}
      <div className="extension-email-signin-unsigned">
        <div className="connect-to-container">
          <div>
            {/* `cdn-src` resolves against `careero.cdnUrl`; the SPA serves its own assets, so
                the path is the src. Width and height are ATTRIBUTES upstream, and the image is
                136×135 — not square, and not what the file's intrinsic size would give. */}
            <img src={asset('/static/assets/img/extension/email-image.png')} width="136" height="135" alt="" />
          </div>
          <div className="connect-to-text">Please connect your work email</div>
          <div className="mT20">
            {/* ⚠️ THE GAP BETWEEN THE BUTTONS IS A WHITESPACE TEXT NODE, NOT A MARGIN. The only
                margin rule here is `.connect-to-email-button + .connect-to-email-button`, which
                pairs Outlook with Exchange and never touches Google — Google's image and the
                Outlook link are separated by nothing but the newline between them in the
                template, collapsed to one space by the inline formatting context. JSX drops
                whitespace between expression children, so the row came out flush and the whole
                thing sat ~4px to the left: 1387 pixels, and it reads as a layout defect. The
                `{' '}` below are that newline. */}
            {/* ⚠️ GOOGLE IS AN IMAGE AND THE OTHER TWO ARE LINKS. Google's brand guidelines
                require their own button artwork, so it is a PNG with an inline height of
                38.5px — a fractional pixel, and the one on which the row's height lands. The
                commented-out `<a class="btn connect-to-email-button">` beside it upstream is
                the version that was replaced; restoring it would be a redesign. */}
            {!providers.gmail ? null : (
              <img
                src={asset('/static/assets/img/extension/btn-google-signin.png')}
                style={{ cursor: 'pointer', height: '38.5px' }}
                alt="Sign in with Google"
                role="button"
                tabIndex={0}
                onClick={() => {
                  onConnect?.('gmail')
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onConnect?.('gmail')
                }}
              />
            )}{' '}
            {!providers.outlook ? null : (
              <a
                href=""
                className="btn connect-to-email-button"
                onClick={(event) => {
                  event.preventDefault()
                  onConnect?.('outlook')
                }}
              >
                <span>
                  <i className="fas fa-envelope fa-lg" />
                </span>{' '}
                <span className="mL5">Outlook</span>
              </a>
            )}{' '}
            {!providers.exchange ? null : (
              <a
                href=""
                className="btn connect-to-email-button"
                onClick={(event) => {
                  event.preventDefault()
                  onConnect?.('exchange')
                  if (onExchangeSignIn !== undefined) setExchangeOpen(true)
                }}
              >
                <span>
                  <i className="fas fa-envelope fa-lg" />
                </span>{' '}
                <span className="mL5">Microsoft Exchange</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  // Styled by TYPE again — `extension-alert-candidates-email-signin { display: flex; flex: 1 }`.
  return createElement(
    hostElement,
    flexBasis === undefined ? null : { style: { flexBasis: `${String(flexBasis)}px` } },
    content,
    !exchangeOpen || onExchangeSignIn === undefined ? null : (
      <ExchangeLoginModal
        onSignIn={onExchangeSignIn}
        onCancel={() => {
          setExchangeOpen(false)
        }}
        onConnected={() => {
          // `modalScope.ok()` then the broadcast: the dialog closes before anyone hears it.
          setExchangeOpen(false)
          onExchangeConnected?.()
        }}
      />
    ),
  )
}
